import React, { lazy, Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import UploadSection from 'entities/Upload/ui/UploadSection/UploadSection';
import { faqList } from 'entities/Landing/ui/FAQSection/FAQSection';
import { useAppDispatch, useAppSelector } from 'shared/lib/hooks/redux';
import { sessionNew } from 'entities/Upload/store/upload/reducers/ActionCreator';

// Below-fold sections lazy loaded to reduce initial bundle
const TransformSection = lazy(() => import('entities/Landing/ui/TransformSection/TransformSection'));
const FeaturesSection = lazy(() => import('entities/Landing/ui/FeaturesSection/FeaturesSection'));
const UseCasesSection = lazy(() => import('entities/Landing/ui/UseCasesSection/UseCasesSection'));
const OutcomesSection = lazy(() => import('entities/Landing/ui/OutcomesSection/OutcomesSection'));
const ActionsSection = lazy(() => import('entities/Landing/ui/ActionsSection/ActionsSection'));
const FaqSection = lazy(() => import('entities/Landing/ui/FAQSection/FAQSection'));

const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map(q => ({
        '@type': 'Question',
        name: q.title,
        acceptedAnswer: { '@type': 'Answer', text: q.content },
    })),
};

const appSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'PhoneCheckr',
    url: 'https://phonecheckr.com',
    applicationCategory: 'BusinessApplication',
    description:
        'Free phone number checker and verification tool. Identify if a number is mobile, landline, or VOIP. Look up carrier by phone number. Validate in bulk by uploading a CSV file.',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
};

const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PhoneCheckr',
    url: 'https://phonecheckr.com',
    logo: {
        '@type': 'ImageObject',
        url: 'https://phonecheckr.com/assets/phone-checkr.d08df109d23250c616e5c07ca2f8ca0d.svg',
    },
    contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        url: 'https://phonecheckr.com/contact',
    },
    address: {
        '@type': 'PostalAddress',
        streetAddress: '1045 East Atlantic Ave #202',
        addressLocality: 'Delray Beach',
        addressRegion: 'FL',
        postalCode: '33483',
        addressCountry: 'US',
    },
    sameAs: ['https://www.salesmessage.com'],
};

const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'PhoneCheckr',
    url: 'https://phonecheckr.com',
    potentialAction: {
        '@type': 'SearchAction',
        target: {
            '@type': 'EntryPoint',
            urlTemplate: 'https://phonecheckr.com/?q={search_term_string}',
        },
        'query-input': 'required name=search_term_string',
    },
};

const SalesmessageSection = styled.section`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 10px 20px;
    @media (max-width: 767px) {
        padding: 10px 15px;
    }
`;

const SalesmessageInner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
    max-width: 720px;
    padding: 72px 0;
    @media (max-width: 767px) {
        padding: 45px 0;
    }
    @media (max-width: 415px) {
        padding: 32px 15px;
    }
`;

const SalesmessageHeadline = styled.h2`
    font-size: 28px;
    font-weight: 700;
    color: #121217;
    margin: 0 0 16px;
    line-height: 1.3;
`;

const SalesmessageBody = styled.p`
    font-size: 16px;
    line-height: 1.65;
    color: #374151;
    margin: 0 0 28px;
`;

const SalesmessageCTA = styled.a`
    display: inline-block;
    background-color: #1268fb;
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    padding: 14px 28px;
    border-radius: 8px;
    text-decoration: none;
    transition: background-color 0.15s ease;
    &:hover {
        background-color: #0f56d6;
    }
`;

const HomePage: React.FC = () => {
    const { token } = useAppSelector(state => state.uploadReducer);
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(sessionNew(token ?? ''));
    }, []);

    return (
        <>
            <Helmet>
                <title>Free Phone Number Checker | Mobile, Landline & VOIP</title>
                <meta name="robots" content="index, follow" />
                <meta
                    name="description"
                    content="Free phone number lookup and validator. Check if a number is mobile, landline, or VOIP, look up the carrier name, and validate in bulk by uploading a CSV file."
                />
                <link rel="canonical" href="https://phonecheckr.com/" />
                <meta property="og:title" content="Free Phone Number Checker | Mobile, Landline & VOIP" />
                <meta
                    property="og:description"
                    content="Free phone number lookup and validator. Check if a number is mobile, landline, or VOIP, look up the carrier name, and validate in bulk by uploading a CSV file."
                />
                <meta property="og:url" content="https://phonecheckr.com/" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://phonecheckr.com/assets/og-phonecheckr.png" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:site_name" content="PhoneCheckr" />
                <meta property="og:locale" content="en_US" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta property="twitter:domain" content="phonecheckr.com" />
                <meta name="twitter:title" content="Free Phone Number Checker | Mobile, Landline & VOIP" />
                <meta
                    name="twitter:description"
                    content="Free phone number lookup and validator. Check if a number is mobile, landline, or VOIP, look up the carrier name, and validate in bulk by uploading a CSV file."
                />
                <meta name="twitter:image" content="https://phonecheckr.com/assets/og-phonecheckr.png" />
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(webSiteSchema)}</script>
            </Helmet>
            <UploadSection />
            {/*
              Suspense fallback reserves vertical space for the lazy-loaded
              below-fold sections so real-user CLS stays low. Without a
              reservation, sections pop in with 0-height fallback → real
              height, causing observable shifts on slow networks. The number
              matches the rough combined height of all lazy sections at
              desktop widths; mobile stacks taller but the reservation only
              needs to be >= what will render. `aria-hidden` prevents the
              placeholder from being announced by screen readers.
            */}
            <Suspense fallback={<div aria-hidden="true" style={{ minHeight: '3200px' }} />}>
                <TransformSection />
                <FeaturesSection />
                <UseCasesSection />
                <OutcomesSection />
                <SalesmessageSection>
                    <SalesmessageInner>
                        <SalesmessageHeadline>Ready to clean your phone list?</SalesmessageHeadline>
                        <SalesmessageBody>
                            Upload a CSV and get line type, carrier, and validity for every contact. Results come back
                            in seconds.
                        </SalesmessageBody>
                        <SalesmessageCTA href="/#upload">Check your numbers free →</SalesmessageCTA>
                    </SalesmessageInner>
                </SalesmessageSection>
                <ActionsSection />
                <FaqSection />
            </Suspense>
        </>
    );
};

export default HomePage;
